import {
  Button,
  Col,
  DatePicker,
  Divider,
  Input,
  message,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd"
import React, { useEffect, useRef, useState } from "react"
import PageHeader from "../../../components/Elements/PageHeader"
import { ArrowDownOutlined, DatabaseOutlined, SearchOutlined } from "@ant-design/icons"
import { collection, getDocs, onSnapshot, query, Timestamp, where } from "firebase/firestore"
import { db } from "../../../firebase/config"
import { loadingSpinWithBg } from "../../../components/Elements/LoadingSpinner"
import moment from "moment"
import { useCollection } from "../../../hooks/useCollection"
import Highlighter from "react-highlight-words"
import InvoicePDFPreview from "./Helper/InvoicePDFPreview"
import InvoiceOrders from "../Clients/Helper/InvoiceOrders"
import dayjs from "dayjs"
import HandleDocumentPayment from "./Helper/HandleDocumentPayment"

const { Link, Text } = Typography
const { RangePicker } = DatePicker
const { Option } = Select

export default function AccountingProformas() {
  // get data for manufacturer list
  const { data: manufacturerList, loading: manufacturerLoading } = useCollection("manufacturers")
  const { data: users, loading: usersLoading } = useCollection("users", [
    "group",
    "array-contains",
    "sales",
  ])

  // get current date and convert to Firebase Timestamp
  const startOfMonth = dayjs().startOf("month")
  const endOfMonth = dayjs().endOf("month")
  const startTimestamp = Timestamp.fromDate(startOfMonth.toDate())
  const endTimestamp = Timestamp.fromDate(endOfMonth.toDate())

  // search range state (Firebase Timestamps)
  const [startDate, setStartDate] = useState(startTimestamp)
  const [endDate, setEndDate] = useState(endTimestamp)

  // selected client for search
  const [searchClient, setSearchClient] = useState("all")

  // all fetched invoices
  const [allInvoices, setAllInvoices] = useState([])

  // table loading
  const [tableLoading, setTableLoading] = useState(false)

  // clients list state
  const [clientList, setClientList] = useState([])
  const [clientsLoading, setClientsLoading] = useState(false)

  // global loading
  const [globalLoading, setGloabalLoading] = useState(false)

  //selects the user for search
  const [selectedUser, setSelectedUser] = useState(null)

  const [searchText, setSearchText] = useState("")
  const [searchedColumn, setSearchedColumn] = useState("")
  const searchInput = useRef(null)

  // State to track filtered data for accurate totals
  const [filteredInvoices, setFilteredInvoices] = useState([])

  // handles the available client list
  useEffect(() => {
    setClientsLoading(true)
    let ref = collection(db, "clients")
    let q

    // If a user is selected, filter by user.id. Otherwise, fetch all clients.
    if (selectedUser && selectedUser.id) {
      q = query(ref, where("user", "==", selectedUser.id))
    } else {
      q = query(ref)
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        const proforma = { ...doc.data(), id: doc.id }
        if (proforma.manufacturer?.id !== "aRxWCWK4BYvNKhqPjIVd") {
          results.push(proforma)
        }
      })
      setClientList(results)
      setClientsLoading(false)
    })
    return () => unsubscribe()
  }, [selectedUser])

  // Initialize filtered invoices when allInvoices changes
  useEffect(() => {
    setFilteredInvoices(allInvoices)
  }, [allInvoices])

  // returns global loading indicator if needed
  const returnGlobalLoading = () => {
    return <>{loadingSpinWithBg(75, "Generating File...")}</>
  }

  if (globalLoading) {
    return returnGlobalLoading()
  }

  // on change select for clients
  const clientSearchSelect = (value) => {
    setSearchClient(value)
  }

  // Handler to set the full user object.
  const handleUserSelect = (value) => {
    if (value === "all") {
      setSelectedUser(null)
    } else {
      // Find the full user object by matching the id.
      const userObj = users.find((userItem) => userItem.id === value)
      setSelectedUser(userObj)
    }
  }

  // on datepicker change for export range
  const onDateChange = (dates, dateStrings) => {
    if (dates) {
      const [start, end] = dates
      const startTimestamp = Timestamp.fromDate(start.toDate())
      const endTimestamp = Timestamp.fromDate(end.endOf("day").toDate())
      setStartDate(startTimestamp)
      setEndDate(endTimestamp)
    }
  }

  // gets invoices based on search criteria
  const getInvoices = async () => {
    setTableLoading(true)
    try {
      const ref = collection(db, "proformas")
      let constraints = []

      // Only add date filters if both startDate and endDate are defined.
      if (startDate && endDate) {
        constraints.push(where("documentDate", ">=", startDate))
        constraints.push(where("documentDate", "<=", endDate))
      }

      // Add client filter if a specific client is selected.
      if (searchClient !== "all") {
        constraints.push(where("client.id", "==", searchClient))
      }

      // Add user filter if a specific user is selected.
      if (selectedUser && selectedUser !== "all") {
        constraints.push(where("userId", "==", selectedUser.id))
      }

      // Create the query with all constraints.
      const q = query(ref, ...constraints)

      let results = []
      const querySnapshot = await getDocs(q)
      querySnapshot.forEach((doc) => {
        results.push({ ...doc.data(), id: doc.id })
      })

      setAllInvoices(results)
      setTableLoading(false)
    } catch (error) {
      message.error(`Error: ${error.message}`)
      console.log(error.message)
      setGloabalLoading(false)
    }
  }

  // Calculate totals for the table footer
  const calculateTotals = (invoices) => {
    const totalNetoSum = invoices.reduce(
      (total, invoice) => total + (invoice.totalData?.totalNetoSum || 0),
      0,
    )
    const totalVat = invoices.reduce(
      (total, invoice) => total + (invoice.totalData?.totalVat || 0),
      0,
    )
    const totalSum = invoices.reduce(
      (total, invoice) => total + (invoice.totalData?.totalSum || 0),
      0,
    )
    return { totalNetoSum, totalVat, totalSum }
  }

  // Handle table changes (including filtering, sorting, pagination)
  const handleTableChange = (pagination, filters, sorter, extra) => {
    // extra.currentDataSource contains the filtered data after all filters are applied
    if (extra && extra.currentDataSource) {
      setFilteredInvoices(extra.currentDataSource)
    } else {
      // Fallback to allInvoices if currentDataSource is not available
      setFilteredInvoices(allInvoices)
    }
  }

  // transform filters for table
  let manufacturerFilter =
    manufacturerList &&
    manufacturerList.map((item) => ({
      text: item.company,
      value: item.company, // use company name as the filter value
    }))

  // Handle search on filter dropdown
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters) => {
    clearFilters()
    setSearchText("")
  }

  // Reusable function to add search props to a column
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}>
            Search
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : "",
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  })

  // columns for the table
  const defaultColumns = [
    {
      title: "Number",
      dataIndex: "docNumber",
      width: "5%",
      align: "left",
      ...getColumnSearchProps("docNumber"),
    },
    {
      title: "Date",
      dataIndex: ["documentDate", "seconds"],
      width: "10%",
      align: "left",
      render: (value) => <Text>{moment.unix(value).format("DD.MM.YYYY")}</Text>,
    },
    {
      title: "Series",
      dataIndex: "docPrefix",
      width: "5%",
      align: "left",
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "left",
      width: "15%",
      sorter: (a, b) => a.client.client.localeCompare(b.client.client),
      render: (value, record) => <Link onClick={() => alert(record.client.id)}>{value}</Link>,
    },
    {
      title: "Manufacturer",
      dataIndex: ["manufacturer", "company"], // access the company field
      align: "left",
      width: "10%",
      sorter: (a, b) => a.manufacturer.company.localeCompare(b.manufacturer.company),
      filters: manufacturerFilter,
      onFilter: (value, record) => record.manufacturer.company === value, // compare company names
      render: (value, record) => <>{record.manufacturer.company}</>,
    },
    {
      title: "Neto (eur)",
      dataIndex: ["totalData", "totalNetoSum"],
      width: "10%",
      align: "left",
      render: (value) => <Text>{value}</Text>,
    },
    {
      title: "Vat (eur)",
      dataIndex: ["totalData", "totalVat"],
      width: "10%",
      align: "left",
      render: (value) => <Text>{value}</Text>,
    },
    {
      title: "Total (eur)",
      dataIndex: ["totalData", "totalSum"],
      width: "10%",
      align: "left",
      render: (value) => <Text>{value}</Text>,
    },
    {
      title: "Status",
      dataIndex: "paymentStatus",
      width: "5%",
      align: "center",
      render: (value, record) => (
        <HandleDocumentPayment
          record={record}
          collectionName="proformas"
          onPaymentUpdate={getInvoices}
        />
      ),
    },
    {
      title: "Orders",
      dataIndex: "orderReferenceId",
      width: "10%",
      align: "left",
      render: (value) => <InvoiceOrders orderReferenceIds={value} />,
    },
    {
      title: "Has Invoice",
      dataIndex: "invoiceIds", // you can use any dataIndex; we'll use record.invoiceIds
      width: "15%",
      align: "left",
      render: (invoiceIds, record) => {
        if (!invoiceIds || invoiceIds.length === 0) {
          return <Text>No invoices</Text>
        }
        return (
          <Space>
            {record.invoiceNumbers && record.invoiceNumbers.length > 0 ? (
              record.invoiceNumbers.map((num, index) => <Tag key={index}>{num}</Tag>)
            ) : (
              <Text>No invoices</Text>
            )}
          </Space>
        )
      },
    },
    {
      title: "Download",
      dataIndex: "downloadUrl",
      width: "5%",
      align: "center",
      render: (value) => (
        <Link href={value}>
          <ArrowDownOutlined />
        </Link>
      ),
    },
    {
      title: "Show",
      dataIndex: "downloadUrl",
      width: "5%",
      align: "center",
      render: (value) => <InvoicePDFPreview pdfUrl={value} />,
    },
    {
      title: "Delete",
      dataIndex: "downloadUrl",
      width: "5%",
      align: "center",
      render: (value, record) => (
        <Button size="small" danger>
          Delete
        </Button>
      ),
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row>
        <Col span={24}>
          <PageHeader text="Proformas" icon={<DatabaseOutlined />} />
        </Col>
        <Col span={24}>
          <Divider orientation="left">Selection</Divider>
          <Space>
            <RangePicker
              format="DD.MM.YYYY"
              defaultValue={[startOfMonth, endOfMonth]}
              onChange={onDateChange}
            />
            <Select
              showSearch
              placeholder="All users selected"
              optionFilterProp="children"
              style={{ width: 250 }}
              defaultValue="all"
              loading={usersLoading}
              onChange={handleUserSelect}
              filterOption={(input, option) =>
                option?.children.toLowerCase().includes(input.toLowerCase())
              }>
              <Option key="all" value="all">
                All Users
              </Option>
              {users &&
                users.map((userItem) => (
                  <Option key={userItem.id} value={userItem.id}>
                    {userItem.email}
                  </Option>
                ))}
            </Select>
            <Select
              showSearch
              placeholder="All clients selected"
              optionFilterProp="children"
              style={{ width: "250px" }}
              defaultValue="all"
              loading={clientsLoading}
              onChange={clientSearchSelect}
              filterOption={(input, option) =>
                option?.children.toLowerCase().includes(input.toLowerCase())
              }>
              <Option key={1} value="all">
                All Clients
              </Option>
              {clientList &&
                clientList.map((client) => (
                  <Option key={client.id} value={client.id}>
                    {client.client}
                  </Option>
                ))}
            </Select>
            <Button onClick={getInvoices}>Search</Button>
          </Space>
        </Col>
        <Col span={24}>
          <Table
            size="small"
            rowKey={(record) => record.id}
            bordered
            dataSource={allInvoices}
            loading={tableLoading || manufacturerLoading}
            columns={defaultColumns}
            onChange={handleTableChange}
            pagination={{ defaultPageSize: 20, showSizeChanger: true }}
            footer={() => {
              // Use filteredInvoices for accurate totals based on current filters
              const { totalNetoSum, totalVat, totalSum } = calculateTotals(filteredInvoices)
              return (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Space>
                    <Tag color="blue">Total Neto (eur): {totalNetoSum.toFixed(2)}</Tag>
                    <Tag color="blue">Total Vat (eur): {totalVat.toFixed(2)}</Tag>
                    <Tag color="blue">Total (eur): {totalSum.toFixed(2)}</Tag>
                  </Space>
                </div>
              )
            }}
          />
        </Col>
      </Row>
    </div>
  )
}
